import { toast } from '@crehana/compass.ui/toaster';
import { useEffect, useRef, useState } from 'react';

import { useTranslationCreateAndEditEmailTemplates } from '@/shared/components/email-component/hooks/use-translations';

import { CustomFileUploaderUI } from './components/custom-file-uploader-ui';
import type { FileInputProps } from './types';
import {
  isAcceptedExtension,
  withTypeFromExtension,
  withTypesFromExtension,
} from './utils';

export const InputFile: React.FC<FileInputProps> = ({
  accept = '.png', // Default accepted file types (PNG images).
  file, // File passed down as a prop.
  label, // Label to display for the file input.
  maxSize = 3000, // Maximum file size (default: 3000 KB).
  onLoadFile, // Callback function to handle the loaded file.
  progressFile, // A flag indicating if the file upload is in progress.
  isLoading, // A loading state to show the uploader is processing.
  id, // ID for the component.
  urlFile, // URL for a pre-existing file that can be shown as a preview.
  className, // Additional CSS class for styling the uploader.
  classNameHover, // Additional CSS class for hover effects.
  canDragging, // Flag to allow dragging for file selection.
  hasNoPreview, // Flag to control if a file preview is shown.
  errorGeneral, // General error state for the file upload.
  handleRetry, // Function to handle retry in case of errors.
  isMultiple, // Flag to allow selecting multiple files.
  isResetForm, // Flag to reset the form state.
  handleDeleteRemove, // Function to remove the selected file.
  isErrorUploadFile, // Flag to control if a file preview is shown.
  onInvalidFileFormat, // Callback for consumers that render their own message (e.g. hasNoPreview).
  uploadButtonAlign = 'center', // Alignment of the absolute wrapper that centers the label (TCSTC-4720, AC-4).
}) => {
  const { t } = useTranslationCreateAndEditEmailTemplates(); // Translates text based on the user's language settings.
  const [fileName, setFileName] = useState(''); // State to store the file name.
  const [fileCustom, setFile] = useState<File | undefined>(file); // State to store the selected file.
  const [error, setError] = useState(''); // State for general error handling.
  const [errorExtension, setErrorExtension] = useState(false); // State to track errors related to file extensions.
  const [imagePreview, setImagePreview] = useState<string | undefined>(urlFile); // State for the file preview URL.
  const fileInputRef = useRef<HTMLInputElement | null>(null); // Reference to the file input element for manual file clearing.

  // Removes the selected file and resets the states related to it.
  const handleFileRemove = () => {
    setFile(undefined);
    setImagePreview('');
    setFileName('');
    setError('');
    isErrorUploadFile && isErrorUploadFile(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = ''; // Clear the file input field.
    }
  };

  // Sets the error state for general errors and resets file-related states.
  const handleState = (isError?: boolean) => {
    setError(isError ? 'error' : ''); // Sets the error state based on the parameter.
    setFile(undefined);
    setFileName('');
  };

  // Sets the error state related to file extension errors.
  const handleSetErrorExtension = (isError?: boolean) => {
    setErrorExtension(!!isError); // Updates the extension error state.
    setFileName('');
  };

  // Handles file selection and drag-and-drop events.
  const handleOnChange = (
    event?: React.ChangeEvent<HTMLInputElement>,
    drag?: React.DragEvent<HTMLLabelElement>,
  ) => {
    drag?.preventDefault();

    // Obtaing the file from the drag or input
    const fileDragged = drag?.dataTransfer?.files?.[0];
    const fileSelected = event?.target?.files?.[0];
    const attachment = fileDragged || fileSelected || fileCustom;
    const fileListSelected =
      event?.target?.files || (drag?.dataTransfer?.files as FileList);

    setError('');
    setFileName(attachment?.name || '');

    if (!attachment || !fileListSelected) {
      handleState();
      return;
    }

    // Validate the format by the extension of the name, never by the file
    // type: a phone can give an empty type (TCSTC-4763).
    if (!isAcceptedExtension(attachment.name, accept)) {
      handleSetErrorExtension(true);
      onInvalidFileFormat?.();
      return;
    }

    const size = attachment.size / 1024;
    if (size > maxSize) {
      handleState(true);
      isErrorUploadFile
        ? isErrorUploadFile(true)
        : toast.error(t('FILE_SIZE_ERROR'));
      return;
    }

    // Create preview and update states
    try {
      const objectUrl = URL.createObjectURL(attachment);
      setImagePreview(objectUrl);
      onLoadFile?.(
        isMultiple
          ? withTypesFromExtension(fileListSelected)
          : withTypeFromExtension(attachment),
      );
      setFile(attachment);
    } catch (error) {
      console.error('Error to preview a file ', error);
      handleState(true);
      toast.error(t('FILE_PREVIEW_ERROR'));
    }
  };

  // Effects to handle file and error changes.
  useEffect(() => {
    if (fileCustom && !error && !errorExtension) {
      const objectUrl = URL.createObjectURL(fileCustom); // Generates preview URL for custom file.
      setImagePreview(objectUrl);
    }
  }, [error, errorExtension, fileCustom]);

  useEffect(() => {
    if (urlFile) {
      setImagePreview(urlFile); // Updates the preview if a URL is provided.
    }
  }, [urlFile]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (errorGeneral) {
      setErrorExtension(true); // If a general error occurs, mark extension error.
      handleFileRemove(); // Remove the file if there's an error.
    }
  }, [errorGeneral]);

  useEffect(() => {
    if (progressFile && file) {
      setFileName(file.name); // Updates the file name during the upload progress.
    }
  }, [file, progressFile]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (error && hasNoPreview) {
      isErrorUploadFile
        ? isErrorUploadFile(true)
        : toast.error(t('FILE_SIZE_ERROR')); // Show error toast if file size is invalid.
    }
  }, [error, hasNoPreview]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (isResetForm) {
      handleFileRemove(); // Resets the file when the form is reset.
    }
  }, [isResetForm]);

  return (
    <CustomFileUploaderUI
      id={id} // Unique ID for the uploader component.
      accept={accept} // Allowed file extensions for the file input.
      fileName={fileName} // The name of the selected file.
      imagePreview={imagePreview} // Preview of the selected file (image URL or object).
      error={error} // Error state to indicate if there's an issue with the file.
      errorExtension={errorExtension} // Error state for invalid file extensions.
      isLoading={isLoading} // Loading state for the uploader (e.g., when a file is being uploaded).
      label={label} // Label text displayed for the uploader.
      progressFile={progressFile} // Indicates if there is any progress during the file upload.
      handleOnChange={handleOnChange} // Function to handle changes when a file is selected or dragged.
      handleFileRemove={() => {
        handleFileRemove();
        if (handleDeleteRemove) {
          handleDeleteRemove();
        }
      }} // Function to remove the selected file.
      fileInputRef={fileInputRef} // Reference to the file input element.
      className={className} // Additional CSS classes for styling.
      classNameHover={classNameHover} // Additional CSS classes for hover styling.
      canDragging={canDragging} // Allows dragging to select files if set to true.
      hasNoPreview={hasNoPreview} // Determines if a preview of the file should be displayed.
      onClickReset={() => setErrorExtension(false)} // Resets the error state when clicked.
      handleRetry={() => {
        setErrorExtension(false); // Reset error state and attempt to retry the action.
        handleRetry && handleRetry(); // Calls the retry function passed as a prop if it exists.
      }}
      isMultiple={isMultiple} // Determines if multiple files can be selected.
      uploadButtonAlign={uploadButtonAlign} // Alignment of the absolute wrapper (TCSTC-4720, AC-4).
    />
  );
};
